import { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { applyBranding } from "@/hooks/useBranding";
import { Button } from "@/components/ui/button";
import { PublicPage } from "@/components/public/PublicShell";
import { CvReviewEditor } from "@/components/cv-review/CvReviewEditor";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { UploadCloud, Sparkles, Loader2, CheckCircle2, FileText, Brain, Wand2, MailCheck, LogIn } from "lucide-react";
import { motion } from "framer-motion";

const stepIn = { initial: { opacity: 0, y: 18 }, animate: { opacity: 1, y: 0 }, transition: { type: "spring", stiffness: 200, damping: 24 } };

const STAGES = [
  { icon: FileText, label: "Reading your CV" },
  { icon: Brain, label: "Extracting skills & experience" },
  { icon: Wand2, label: "Building your profile" },
];

export default function PublicOnboarding() {
  const nav = useNavigate();
  const fileRef = useRef();
  const [step, setStep] = useState("upload"); // upload | processing | review | sent
  const [stageIdx, setStageIdx] = useState(0);
  const [sid, setSid] = useState(null);
  const [profile, setProfile] = useState(null);
  const [sentTo, setSentTo] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { applyBranding({ brand_primary: "#10B981" }); }, []);

  const onFile = async (file) => {
    if (!file) return;
    setStep("processing"); setStageIdx(0);
    const ticker = setInterval(() => setStageIdx((i) => Math.min(i + 1, STAGES.length - 1)), 1200);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/public/cv-onboard", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const oid = data.onboard_id;
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        try {
          const { data: s } = await api.get(`/public/cv-onboard/${oid}`);
          if (s.status === "ready") {
            clearInterval(poll); clearInterval(ticker);
            setSid(oid);
            const prof = s.profile || {};
            setProfile({ ...prof, contact: { ...(prof.contact || {}), email: s.email || prof.contact?.email || "" } });
            setStep("review");
          } else if (s.status === "parse_failed") {
            clearInterval(poll); clearInterval(ticker);
            toast.error("We couldn't read that CV. Try a PDF or DOCX export.");
            setStep("upload");
          } else if (tries > 48) {
            clearInterval(poll); clearInterval(ticker);
            toast.error("Parsing is taking longer than usual — please try again.");
            setStep("upload");
          }
        } catch { /* transient — keep polling */ }
      }, 2500);
    } catch (e) {
      clearInterval(ticker);
      toast.error(e.response?.data?.detail || "We couldn't read that CV. Try a PDF or DOCX.");
      setStep("upload");
    }
  };

  const confirm = async (clean) => {
    const email = (clean.contact?.email || "").trim();
    setBusy(true);
    try {
      const { data } = await api.post("/public/onboard-confirm", { onboard_id: sid, email, profile: { ...clean, contact: { ...(clean.contact || {}), email } } });
      setSentTo(email);
      setStep("sent");
      if (!data.sent) toast.message("Profile saved. If the email doesn't arrive, use Sign in on the login page.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not finish onboarding");
    } finally { setBusy(false); }
  };

  return (
    <PublicPage className="bg-[#070a09] text-white">
      <header className="flex items-center justify-between px-6 sm:px-10 h-20">
        <Link to="/" className="flex items-center gap-2.5"><div className="h-9 w-9 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center"><Sparkles className="h-5 w-5 text-white" /></div><span className="font-display font-bold text-lg">HireStream</span></Link>
        <div className="flex items-center gap-2.5">
          <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-emerald-200 bg-emerald-500/10 border border-emerald-400/30 rounded-full px-3 py-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> Already have an account?</span>
          <Link to="/login" data-testid="public-signin" className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full bg-emerald-500 text-white hover:bg-emerald-400 shadow-lg shadow-emerald-500/25 transition-colors">
            <LogIn className="h-4 w-4" /> Sign in
          </Link>
        </div>
      </header>

      <div className={cn("px-4 sm:px-6 py-8 mx-auto", step === "review" ? "max-w-6xl" : "max-w-2xl")}>
        {step === "review" ? (
          <div className="mb-6 rise-in" data-testid="review-intro">
            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-emerald-300 mb-2"><CheckCircle2 className="h-3.5 w-3.5" /> Your profile is ready</div>
            <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">Check what we found</h1>
            <p className="text-sm text-slate-400 mt-2 max-w-2xl">We filled everything in from your CV. Open each section, fix anything that's off, then press Looks good. Nothing is shared until you confirm.</p>
          </div>
        ) : (
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-emerald-300 mb-3"><Wand2 className="h-3.5 w-3.5" /> AI agents at your service — you build nothing</div>
            <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">Just drop your CV. Our AI builds your profile.</h1>
            <p className="text-sm text-slate-400 mt-2">No forms, no account. Agents read your CV, assemble a recruiter-ready profile, and email you a one-click sign-in link.</p>
          </div>
        )}

        {step === "upload" && (
          <motion.div key="upload" {...stepIn} className="relative" data-testid="public-cv-cta">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-amber-300 via-amber-400 to-emerald-400 text-slate-900 text-xs font-bold px-3.5 py-1 shadow-lg shadow-amber-500/30 ring-1 ring-white/20 animate-bounce">
                <Sparkles className="h-3.5 w-3.5" /> Start here — free, 60 seconds
              </span>
            </div>
            <div className="pointer-events-none absolute -inset-0.5 rounded-2xl bg-gradient-to-r from-emerald-500/50 via-teal-400/50 to-emerald-500/50 blur opacity-60 animate-pulse" />
            <div onClick={() => fileRef.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
              data-testid="public-cv-dropzone"
              className="relative rounded-2xl border-2 border-dashed border-emerald-400/50 hover:border-emerald-400 bg-white/[0.03] p-14 text-center cursor-pointer transition-colors">
              <div className="h-20 w-20 rounded-2xl bg-emerald-500/15 mx-auto flex items-center justify-center mb-5"><UploadCloud className="h-10 w-10 text-emerald-400" /></div>
              <div className="text-lg font-medium">Drop your CV here or click to browse</div>
              <div className="text-xs text-slate-400 mt-1">PDF, DOCX or TXT · parsed instantly by AI</div>
              <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.doc" className="hidden" data-testid="public-cv-input" onChange={(e) => onFile(e.target.files?.[0])} />
            </div>
          </motion.div>
        )}

        {step === "processing" && (
          <motion.div key="processing" {...stepIn} className="rounded-2xl border border-white/10 bg-white/[0.03] p-10 text-center" data-testid="public-cv-processing">
            <div className="relative h-20 w-20 mx-auto mb-6">
              <div className="absolute inset-0 rounded-full border-2 border-emerald-500/20" />
              <div className="absolute inset-0 rounded-full border-2 border-emerald-400 border-t-transparent animate-spin" />
              <div className="absolute inset-0 flex items-center justify-center"><Sparkles className="h-7 w-7 text-emerald-400 animate-pulse" /></div>
            </div>
            <div className="text-lg font-display font-semibold mb-5">Building your profile…</div>
            <div className="space-y-2.5 max-w-xs mx-auto text-left">
              {STAGES.map((s, i) => { const Icon = s.icon; const done = i < stageIdx, current = i === stageIdx; return (
                <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: i <= stageIdx ? 1 : 0.4, x: 0 }} transition={{ delay: i * 0.08 }} className="flex items-center gap-3 text-sm">
                  {done ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : current ? <Loader2 className="h-4 w-4 text-emerald-400 animate-spin" /> : <Icon className="h-4 w-4 text-slate-500" />}{s.label}
                </motion.div>); })}
            </div>
          </motion.div>
        )}

        {step === "review" && profile && (
          <motion.div key="review" {...stepIn} data-testid="public-cv-review">
            <CvReviewEditor profile={profile} setProfile={setProfile} gated requireEmail busy={busy} onSubmit={confirm}
              submitLabel="Confirm & email my link" submitTestId="public-confirm"
              onCancel={() => { setStep("upload"); setProfile(null); }} cancelLabel="Upload a different CV" cancelTestId="public-reupload" />
          </motion.div>
        )}

        {step === "sent" && (
          <motion.div key="sent" {...stepIn} className="max-w-md mx-auto text-center" data-testid="public-sent">
            <motion.div initial={{ scale: 0.4, rotate: -12 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 260, damping: 14, delay: 0.1 }} className="relative h-28 w-28 mx-auto mb-6">
              <div className="absolute inset-0 rounded-full bg-emerald-500/20 blur-2xl animate-pulse" />
              <div className="absolute inset-2 rounded-full border border-emerald-400/30 animate-ping" />
              <div className="relative h-full w-full rounded-3xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-2xl shadow-emerald-500/30"><MailCheck className="h-12 w-12 text-white" /></div>
            </motion.div>
            <h2 className="text-2xl font-display font-bold">Check your inbox</h2>
            <p className="text-sm text-slate-400 mt-2">We sent a one-click sign-in link to</p>
            <div className="mt-1.5 inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-4 py-1.5 text-sm font-medium text-emerald-200">{sentTo}</div>

            <div className="mt-7 grid grid-cols-3 gap-2 text-left">
              {[["1", "Open the email", "from HireStream"], ["2", "Tap the link", "valid 30 min"], ["3", "You're in", "no password"]].map(([n, t, s]) => (
                <div key={n} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                  <div className="h-6 w-6 rounded-full bg-emerald-500/15 text-emerald-300 text-xs font-bold flex items-center justify-center mb-2">{n}</div>
                  <div className="text-xs font-medium">{t}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{s}</div>
                </div>
              ))}
            </div>

            <Button onClick={() => nav("/login")} data-testid="sent-signin" className="w-full mt-7 gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-white shadow-lg shadow-emerald-500/25"><LogIn className="h-4 w-4" /> Go to sign in</Button>
            <p className="text-xs text-slate-500 mt-3">Didn't get it? Check spam, or <Link to="/login" className="text-emerald-300 hover:underline">use Sign in</Link> to request a new link.</p>
          </motion.div>
        )}
      </div>
    </PublicPage>
  );
}
